// ============================================================
// DOMAIN TYPES — Data contracts theo 07_DATA_SCHEMA.md
// ============================================================

// --- RNG ---
export interface RNG {
  next(): number; // [0, 1)
}

// --- Coordinates ---
export interface LatLon {
  lat: number;
  lon: number;
}

export interface WorldPoint {
  x: number; // meters từ center
  y: number; // meters từ center
}

// --- Player ---
export interface PlayerState {
  id: string;
  username: string;   // Tên người chơi / biệt danh
  stallName: string;  // Tên quán nước
  money: number;
  reputation: number; // [0, 100]
  heat: number;       // [0, 1]
  rank: string;
  prestige: number;
  neighborhoodId: string;
  ownedStalls: string[]; // stall ids
  activeMenu: string[];  // IDs các món đang mở bán
  upgrades: Record<string, number>;
  totalRevenue: number;
  totalProfit: number;
  totalSales: number;
  secretKey?: string | undefined;
  stallLevel?: number | undefined;
  unlockedPerks?: string[] | undefined;
  hasStaff?: boolean | undefined; // Thuê phụ quán (tăng năng lực phục vụ, tự tin cân ca đêm)
}

// --- Neighborhood DNA ---
export interface NeighborhoodDNA {
  density: number;        // [0,1]
  roadTraffic: number;    // [0,1]
  pedestrianFlow: number; // [0,1]
  schoolFlow: number;     // [0,1]
  hospitalFlow: number;   // [0,1]
  marketFlow: number;     // [0,1]
  nightFlow: number;      // [0,1]
  competition: number;    // [0,1]
  risk: number;           // [0,1]
}

// --- Road ---
export type RoadClass =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'residential'
  | 'living_street'
  | 'footway'
  | 'unknown';

export interface Road {
  id: string;
  points: WorldPoint[];
  roadClass: RoadClass;
  name?: string;
}

// --- POI ---
export type POIType = 'school' | 'hospital' | 'bus_stop' | 'market' | 'real_stall' | 'other';

export interface POI {
  id: string;
  type: POIType;
  position: WorldPoint;
  name?: string;
}

// --- Business Node ---
export interface BusinessNode {
  id: string;
  x: number;
  y: number;
  sourcePoiId?: string;
  roadType?: RoadClass;
  trafficBase: number;  // [0,1]
  visibility: number;   // [0,1]
  density: number;      // [0,1]
  risk: number;         // [0,1]
  unlockCost: number;   // VND
  rentPerDay: number;   // VND
  tags: string[];
  ownerId?: string | undefined;
}

// --- Stall ---
export interface StallBranch {
  capacity: number;
  speed: number;
  brand: number;
}

export interface Stall {
  id: string;
  ownerId: string;
  name?: string;
  nodeId: string;
  level: number;
  price: number;   // VND/khách
  chairs: number;
  branch: StallBranch;
  reputation: number; // [0, 100]
  lastActiveAt: number; // timestamp ms
  confiscatedChairs?: number | undefined;
  isSuspended?: boolean | undefined;
  suspendedUntil?: number | undefined;
}

// --- Ghost Stall ---
export interface GhostStallPayload {
  uid: string;
  sid: string;
  cell: string;
  x: number;
  y: number;
  name: string;
  level: number;
  chairs: number;
  price: number;
  rep: number;
  activeAt: number;
}

// --- Dummy Rivals (Quán Đối Thủ Thuật Toán Xâm Chiếm Mặt Bằng) ---
export type DummyRivalStatus = 'booming' | 'stable' | 'struggling' | 'bankrupt';

export interface DummyRival {
  id: string;
  nodeId: string;
  name: string;
  level: number;
  reputation: number;
  status: DummyRivalStatus;
  dailyRevenue: number;       // Doanh số hàng ngày (VND)
  dailyProfit: number;        // Lợi nhuận hàng ngày (VND)
  monthlyProfit: number;      // Lợi nhuận tháng (VND) = dailyProfit * 30
  transferFee: number;        // Tiền sang lại quán (>= 3 tháng lãi nếu tăng trưởng, 0đ nếu phá sản)
  specialty: string;          // Món chủ lực
  establishedDay: number;     // Ngày bắt đầu chiếm đóng
}

export interface WaterFeature {
  type: 'lake' | 'river';
  points: WorldPoint[];
  name?: string;
}

// --- Neighborhood ---
export interface Neighborhood {
  id: string;
  center: LatLon;
  radiusM: 500;
  label: string;
  dna: NeighborhoodDNA;
  roads: Road[];
  pois: POI[];
  nodes: BusinessNode[];
  waterFeature?: WaterFeature | undefined;
}

// --- Shifts ---
export type DayShift = 'morning' | 'noon' | 'afternoon' | 'evening' | 'night';

// --- Events ---
export type EventType =
  | 'rain'
  | 'raid'
  | 'price_war'
  | 'unpaid_bill'
  | 'rival_sabotage'
  | 'street_brawl'
  | 'extreme_weather'
  | 'viral_tiktok';

export interface EventOption {
  id: string;
  label: string;
  moneyDelta?: number;
  heatDelta?: number;
  trafficDelta?: number;
  reputationDelta?: number;
}

export interface GameEvent {
  id: string;
  type: EventType;
  trigger: 'time' | 'heat_threshold' | 'random';
  durationMs: number;
  options: EventOption[];
  severity: number; // [0,1]
}

export interface ActiveEvent {
  id: string;
  type: EventType;
  startedAt: number;
  expiresAt: number;
  severity: number;
  optionIds: string[];
}

// --- Game State Machine ---
export type GamePhase =
  | 'BOOT'
  | 'LOBBY'
  | 'LOCATION_SELECT'
  | 'MAP_LOADING'
  | 'MAP_READY'
  | 'PLAYING'
  | 'EVENT_ACTIVE'
  | 'INSPECT_NODE'
  | 'INSPECT_RIVAL'
  | 'PAUSED'
  | 'MILESTONE'
  | 'PRESTIGE';

// --- Sales Transaction ---
export interface SaleTransaction {
  id: string;
  itemName: string;
  icon: string;
  sellPrice: number;
  costPrice: number;
  profit: number;
  time: number;
  isTakeaway?: boolean;
}

// --- Daily Stats ---
export interface DailyStats {
  dayNumber: number;
  startedAt: number;     // timestamp khi bắt đầu ngày
  revenue: number;       // tổng tiền bán (sum sellPrice)
  cost: number;          // tổng vốn chi (sum costPrice)
  profit: number;        // revenue - cost
  salesCount: number;    // số đơn hàng (không dùng cốc)
  rentPaid: number;      // tiền thuê mặt bằng hôm đó
  wasteCost?: number;    // tiền hao hụt đồ tươi pha sẵn hỏng hủy cuối ngày
  depreciationCost?: number; // tiền khấu hao vật tư thiết bị (bàn ghế, dù, thùng đá, phin)
  staffCost?: number;    // chi phí lương nhân viên phụ quán (120k/ngày)
}

// --- Weather ---
export type WeatherType = 'sunny' | 'cloudy' | 'rainy' | 'hot' | 'windy';

export interface WeatherState {
  type: WeatherType;
  name: string;
  emoji: string;
  trafficMultiplier: number; // e.g. sunny=1.0, rainy=0.5, hot=0.8, cloudy=0.9, windy=0.7
  description: string;
}

// --- Upgrades ---
export type UpgradeId = 'umbrella' | 'ice_machine' | 'signboard' | 'speaker';

export interface UpgradeDefinition {
  id: UpgradeId;
  name: string;
  emoji: string;
  description: string;
  cost: number;
  trafficBonus: number; // multiplier bonus e.g. 0.15 = +15%
  reputationBonus: number;
}

// --- Milestone ---
export interface Milestone {
  id: string;
  message: string;
  emoji: string;
}

// --- Trends ---
export interface TrendItem {
  query: string;
  headline?: string;
  traffic?: string;
  source?: string;
  url?: string;
  picture?: string;
  pubDate?: string;
}

// --- Full Game State ---
export interface GameState {
  phase: GamePhase;
  tick: number;
  lastTickAt: number;
  player: PlayerState;
  neighborhood: Neighborhood | null;
  stalls: Record<string, Stall>;
  ghostStalls: GhostStallPayload[];
  dummyRivals: Record<string, DummyRival>; // Quán đối thủ dummy thuật toán chiếm đóng
  activeEvent: ActiveEvent | null;
  inspectNodeId: string | null;
  inspectRivalId: string | null;
  narrativeQuote: string | null;
  currentTrendingTopic?: string;
  trendingItems?: TrendItem[];
  seed: number; // RNG seed
  recentSales: SaleTransaction[];
  latestSale: SaleTransaction | null;
  loadingMessage: string | null;
  visualCustomers?: import('../simulation/customerAggregate.ts').VisualCustomer[] | undefined;
  dayNumber: number;
  dayStats: DailyStats;
  dayHistory: DailyStats[];
  showDayReport: boolean;
  weather: WeatherState;
  pendingMilestone: Milestone | null;
  currentShift: DayShift;
  isWeekend: boolean;
  isOvertimeActive: boolean;
  dayOfWeek: string;
  pendingCelebration: UnlockCelebration | null;
  isPaused?: boolean | undefined;
}

export type { VisualCustomer } from '../simulation/customerAggregate.ts';

export interface UnlockCelebration {
  type: 'level' | 'day' | 'survival';
  levelOrDay: number;
  title: string;
  subtitle: string;
  items: Array<{
    name: string;
    icon: string;
    desc: string;
    type: 'menu' | 'perk';
    id: string;
  }>;
}

export function formatZoneTag(tag?: string): string {
  if (!tag) return 'vỉa hè góc phố';
  const dict: Record<string, string> = {
    residential: 'khu dân cư',
    commercial: 'khu thương mại',
    market: 'khu chợ truyền thống',
    office: 'khu văn phòng',
    park: 'bờ hồ / công viên',
    school: 'khu trường học',
    hospital: 'khu bệnh viện',
    industrial: 'khu công nghiệp',
    tourism: 'địa điểm du lịch',
    sidewalk: 'vỉa hè',
    street: 'mặt đường',
  };
  return dict[tag.toLowerCase()] ?? tag;
}
